import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ChatCircleDots, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Page for anonymous finders to find back their conversations using the local finder_token.
 * The token is stored per-conversation under key `finder_token_{conv_id}`.
 * We also store a global "finder_master_tokens" array of report-tokens.
 */
export default function FinderHub() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const tokens = new Set();
      // Collect all tokens stored locally
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("finder_token_")) tokens.add(localStorage.getItem(k));
      }
      const reportToken = localStorage.getItem("finder_report_token");
      if (reportToken) tokens.add(reportToken);

      const all = [];
      for (const t of tokens) {
        try {
          const { data } = await api.get("/finder/conversations", { params: { finder_token: t } });
          data.forEach((c) => { if (!all.find((x) => x.conversation_id === c.conversation_id)) all.push({ ...c, token: t }); });
        } catch (e) { console.warn("Finder token no longer valid", e); }
      }
      setConversations(all);
      setLoading(false);
    })();
  }, []);

  return (
    <Layout>
      <SEO title="Mes conversations" description="Suivez les conversations avec les familles concernées par les doudous que vous avez trouvés." />
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="finder-hub-page">
        <h1 className="font-display text-3xl font-black text-stone-800" data-testid="finder-hub-title">Mes conversations</h1>
        <p className="text-stone-600 mt-2">Les familles concernées par les doudous que vous avez signalés vous écrivent ici.</p>

        {loading ? (
          <p className="mt-8 text-stone-500">Chargement...</p>
        ) : conversations.length === 0 ? (
          <div className="mt-8 bg-white rounded-3xl border border-rose-100 p-10 text-center">
            <ChatCircleDots size={56} weight="duotone" className="mx-auto text-rose-300" />
            <p className="mt-4 text-stone-600">Aucune conversation pour le moment.</p>
            <p className="text-sm text-stone-500 mt-2">Si vous avez signalé un doudou trouvé, attendez qu'une famille le reconnaisse — vous serez alerté ici.</p>
            <Link to="/trouve" className="inline-block mt-6">
              <Button variant="outline" className="rounded-full font-bold border-2 border-rose-300 text-rose-700"><MagnifyingGlass size={18} className="mr-2" /> Signaler un doudou trouvé</Button>
            </Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {conversations.map((c) => (
              <Link
                key={c.conversation_id}
                to={`/conversations/${c.conversation_id}`}
                onClick={() => localStorage.setItem(`finder_token_${c.conversation_id}`, c.token)}
                className="block bg-white rounded-2xl border border-rose-100 p-5 hover:shadow-md transition"
                data-testid={`finder-conv-${c.conversation_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-stone-800">{c.owner_first_name}</p>
                    <p className="text-sm text-stone-500">À propos d'un doudou que vous avez trouvé</p>
                  </div>
                  <p className="text-xs text-stone-400">{new Date(c.last_message_at).toLocaleString("fr-FR")}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
